import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Spinner,
  Text,
  Checkbox,
  Select,
  useToast
} from '@chakra-ui/react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const AdminCRUCliente = () => {
  const { id: clienteId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const { user, accessToken } = useAuth();
  const { t } = useTranslation();
  
  // Si el usuario es un cliente y no hay clienteId en la URL, usar su propio ID y modo lectura
  const isClienteProfile = user?.user_type === 'cliente' && !clienteId;
  const effectiveClienteId = isClienteProfile ? user?.user_id : clienteId;
  const isReadOnly = searchParams.get('mode') === 'read' || isClienteProfile;
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nombre: '',
    apellidos: '',
    telefono: '',
    habilitado: true, // Por defecto true al crear
    acepto_politicas: false,
    forma_pago: 'Bizum', // Por defecto 'Bizum'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isEditing = !!effectiveClienteId; // Determina si estamos en modo de edición

  useEffect(() => {
    const fetchCliente = async () => {
      if (isEditing) {
        setLoading(true);
        try {
          const baseURL = process.env.REACT_APP_API_BASE_URL;
          const response = await fetch(`${baseURL}/api/clientes/${effectiveClienteId}`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          });
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Error fetching cliente: ${response.status} - ${errorData?.detail || response.statusText}`);
          }
          const data = await response.json();
          // Asegúrate de que 'password' no se cargue, ya que no se muestra ni se edita directamente
          // y los campos que son booleanos o tienen valores por defecto se ajusten al tipo
          setFormData({
            ...data,
            password: '', // Siempre limpiar el campo de contraseña al cargar para edición
            habilitado: data.habilitado ?? true, // Si no viene, que sea true por defecto
            acepto_politicas: data.acepto_politicas ?? false, // Si no viene, que sea false por defecto
            forma_pago: data.forma_pago || 'Bizum' // Si no viene, que sea 'Bizum' por defecto
          });
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchCliente();
  }, [isEditing, effectiveClienteId, accessToken]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);


    const baseURL = process.env.REACT_APP_API_BASE_URL;
    const url = isEditing ? `${baseURL}/api/clientes/${effectiveClienteId}` : `${baseURL}/api/clientes/`;
    const method = isEditing ? 'PUT' : 'POST';

    // Preparar los datos a enviar
    const dataToSend = { ...formData };

    // Si estamos editando y el campo de contraseña está vacío, no enviarlo
    if (isEditing && dataToSend.password === '') {
      delete dataToSend.password;
    }
    // Si estamos creando, la contraseña debe ser enviada
    if (!isEditing && dataToSend.password === '') {
        setError('La contraseña es obligatoria para nuevos clientes.');
        setLoading(false);
        return;
    }

    // Eliminar campos que no deben ser enviados al backend (solo lectura o gestionados por el servidor)
    const fieldsToExclude = ['id', 'fecha_creacion', 'ultimo_login', 'hashed_password', 'codigo_verificacion_email'];
    fieldsToExclude.forEach(field => delete dataToSend[field]);

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend), // Enviar los datos preparados
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Error ${isEditing ? 'editando' : 'creando'} cliente: ${response.status} - ${errorData?.detail || response.statusText}`);
      }

      const data = await response.json();
      toast({
        title: isEditing ? 'Cliente actualizado' : 'Cliente creado',
        description: `Cliente ${isEditing ? 'actualizado' : 'creado'} con éxito: ${data.email}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
        containerStyle: {
          color: 'black'
        }
      });
      setTimeout(() => navigate('/admin/clientes'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center" minH="200px" className="admin-loading">
        <Spinner size="xl" />
        <Text ml={4} className="admin-loading-text">{t('conductor_cargando_servicios')}</Text>
      </Box>
    );
  }

  return (
    <Box p={4} borderWidth="1px" borderRadius="lg" maxWidth="600px" mx="auto" className="admin-page-container">
      <Heading as="h2" size="lg" mb={4} className="admin-page-title">
        {isReadOnly ? t('admin_cliente_titulo_detalles') : isEditing ? t('admin_cliente_titulo_editar') : t('admin_cliente_titulo_crear')}
      </Heading>
      {error && (
        <Box bg="red.100" color="black" p={4} borderRadius="md" mb={4} className="admin-alert admin-alert-error">
          <Text fontWeight="bold">Error:</Text>
          <Text>{error}</Text>
        </Box>
      )}
      <form onSubmit={handleSubmit} className="admin-form-container">
        <FormControl isRequired mb={4}>
          <FormLabel htmlFor="email" className="admin-form-label">{t('admin_campo_email')}</FormLabel>
          <Input type="email" id="email" name="email" value={formData.email} onChange={handleChange} isReadOnly={isReadOnly} className="admin-form-input" bg="white" color="black" />
        </FormControl>

        {!isReadOnly && (
          <FormControl mb={4}>
            <FormLabel htmlFor="password" className="admin-form-label">
              {isEditing ? t('admin_campo_nuevo_password') : t('admin_campo_password')}
            </FormLabel>
            <Input 
              type="password" 
              id="password" 
              name="password" 
              value={formData.password} 
              onChange={handleChange} 
              placeholder={isEditing ? t('admin_placeholder_password_opcional') : ''}
              className="admin-form-input" 
              bg="white" 
              color="black" 
            />
            {isEditing && (
              <Text fontSize="sm" color="gray.600" mt={1}>
                {t('admin_ayuda_password_edicion')}
              </Text>
            )}
          </FormControl>
        )}

        <FormControl isRequired mb={4}>
          <FormLabel htmlFor="nombre" className="admin-form-label">{t('admin_campo_nombre')}</FormLabel>
          <Input type="text" id="nombre" name="nombre" value={formData.nombre} onChange={handleChange} isReadOnly={isReadOnly} className="admin-form-input" bg="white" color="black" />
        </FormControl>

        <FormControl mb={4}>
          <FormLabel htmlFor="apellidos" className="admin-form-label">{t('admin_campo_apellidos')}</FormLabel>
          <Input type="text" id="apellidos" name="apellidos" value={formData.apellidos} onChange={handleChange} isReadOnly={isReadOnly} className="admin-form-input" bg="white" color="black" />
        </FormControl>

        <FormControl isRequired mb={4}> {/* 'telefono' ahora es requerido */}
          <FormLabel htmlFor="telefono" className="admin-form-label">{t('admin_campo_telefono')}</FormLabel>
          <PhoneInput
            country={'es'}
            value={formData.telefono}
            onChange={phone => setFormData(prev => ({ ...prev, telefono: '+' + phone }))}
              inputProps={{
                name: 'telefono',
                required: true,
                autoFocus: false,
                disabled: isReadOnly
              }}
              inputStyle={{
                width: '100%',
                background: '#fff',
                color: '#000', // Letras en negro
              }}
            specialLabel=""
            disabled={isReadOnly}
          />
        </FormControl>

        <FormControl display="flex" alignItems="center" mb={4}>
          <FormLabel htmlFor="habilitado" mr={2} className="admin-form-label">{t('admin_campo_habilitado')}</FormLabel>
          <Checkbox id="habilitado" name="habilitado" isChecked={formData.habilitado} onChange={handleChange} isReadOnly={isReadOnly} isDisabled={isReadOnly} />
        </FormControl>

        <FormControl isRequired display="flex" alignItems="center" mb={4}> {/* 'acepto_politicas' ahora es requerido */}
          <FormLabel htmlFor="acepto_politicas" mr={2} className="admin-form-label">{t('admin_campo_acepto_politicas')}</FormLabel>
          <Checkbox id="acepto_politicas" name="acepto_politicas" isChecked={formData.acepto_politicas} onChange={handleChange} isReadOnly={isReadOnly} isDisabled={isReadOnly} />
        </FormControl>

        <FormControl mb={4}>
          <FormLabel htmlFor="forma_pago" className="admin-form-label">{t('admin_campo_forma_pago')}</FormLabel>
          <Select id="forma_pago" name="forma_pago" value={formData.forma_pago} onChange={handleChange} bg="white" color="black" sx={{ option: { bg: 'white', color: 'black' } }} isReadOnly={isReadOnly} isDisabled={isReadOnly}>
            <option value="Efectivo">Efectivo</option>
            <option value="Bizum">Bizum</option>
            <option value="Tarjeta">Tarjeta</option>
          </Select>
        </FormControl>

        {!isReadOnly && (
          <>
            <Button isLoading={loading} type="submit" colorScheme="blue" className="admin-button-primary">{isEditing ? t('admin_boton_actualizar_cliente') : t('admin_boton_crear_cliente')}</Button>
            <Button ml={2} onClick={() => navigate('/admin/clientes')} className="admin-button-secondary">{t('admin_boton_cancelar')}</Button>
          </>
        )}
        {isReadOnly && (
          <Button onClick={() => navigate(isClienteProfile ? '/' : '/admin/reservas')} className="admin-button-secondary">
            {t(isClienteProfile ? 'volver_inicio' : 'admin_boton_volver_reservas')}
          </Button>
        )}
      </form>
    </Box>
  );
};

export default AdminCRUCliente;